import { headerRow, resolveLinks, subpageRows, type CompileResult, type Subpage, type Warning } from '@textbox/core';
import { ScreenRenderer, sizeScreen } from '@textbox/renderer';
import { el } from './ui.ts';

/**
 * A teletext preview: the real renderer at a small cell size, one tab per
 * subpage, and the compiler's warning list underneath.
 */
export class Preview {
  root: HTMLElement;
  private screenEl: HTMLElement;
  private renderer: ScreenRenderer;
  private tabs: HTMLElement;
  private warningsEl: HTMLElement;
  private subpages: Subpage[] = [];
  private current = 0;
  private cw: number;

  constructor(cw = 10) {
    this.cw = cw;
    this.screenEl = el('div', { class: 'screen', 'aria-label': 'Preview' });
    this.tabs = el('div', { class: 'tabs' });
    this.warningsEl = el('ul', { class: 'warnings' });
    this.root = el('div', { class: 'preview' }, this.tabs, this.screenEl, this.warningsEl);
    this.renderer = new ScreenRenderer(this.screenEl);
    sizeScreen(this.screenEl, cw);
    this.blank();
  }

  blank(): void {
    this.subpages = [];
    this.renderer.render(Array.from({ length: 25 }, () => new Array<number>(40).fill(0x20)), { reveal: true, mix: false, links: [], hover: null, addressColour: 7 });
    this.tabs.replaceChildren();
    this.warningsEl.replaceChildren();
  }

  show(result: CompileResult, service = 'TEXTBOX'): void {
    this.subpages = result.subpages;
    if (this.current >= this.subpages.length) this.current = 0;
    this.tabs.replaceChildren(
      ...this.subpages.map((sp, i) => el('button', { class: i === this.current ? 'tab on' : 'tab', onClick: () => { this.current = i; this.show(result, service); } }, `${sp.page} ${sp.sub}/${sp.subCount}`)),
    );
    const sp = this.subpages[this.current];
    if (!sp) { this.blank(); return; }
    const rows = subpageRows(sp);
    rows[0] = headerRow({ service, mode: 'idle', page: sp.page, sub: sp.sub, subCount: sp.subCount, now: new Date() });
    this.renderer.render(rows, { reveal: true, mix: false, links: resolveLinks(sp, rows), hover: null, addressColour: 7, enhancements: sp.enhancements });
    this.warningsEl.replaceChildren(...result.warnings.map((w: Warning) => el('li', { class: `w-${w.code}` }, el('code', {}, w.code), ' ', w.message, w.sub ? ` (subpage ${w.sub})` : '')));
  }
}
